"use client";

import React, { useState, useEffect, lazy, Suspense, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/account/Navbar";
import { CloudDownload } from "lucide-react";
import PetProfileView from "../../components/petProfiles/PetProfileView";
const MedicalRecordView = lazy(() => import("../../components/petProfiles/MedicalRecordView"));
import BehavioralDataView from "../../components/petProfiles/BehavioralDataView";
import { usePets } from "../../hooks/usePets";
import Footer from "../../components/account/Footer";
import { useAuth } from "../../hooks/useAuth";
import { USER_TYPES } from "../../constants/userTypes";
import api, { petAPI } from "../../services/api";
import { pdfServices } from "../../services/pdfservices";
import { showErrorToast } from "../../utils/notificationUtils";
import SOAPDetailsContent from "../../components/reception/SOAPDetailsContent";
import RestrictedVisitModal from "../../components/reception/RestrictedVisitModal";
import AttachmentsModal from "../../components/modals/AttachmentsModal";
import GuardianshipList from "../../components/petProfiles/GuardianshipList";
import "./PetStyles.css";
import "./PetProfilePage.css";

// Footer Component (same as other pages)
const FooterOld = () => (
  <footer
    style={{
      background: "#FFFFFF",
      borderTop: "1px solid #E5E7EB",
      padding: "40px 60px",
      marginTop: "80px",
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        maxWidth: "1400px",
        margin: "0 auto",
      }}
    >
      <div
        style={{
          fontFamily: '"Red Hat Display", sans-serif',
          fontSize: "14px",
          color: "#6B7280",
        }}
      >
        All rights reserved to:{" "}
        <span style={{ fontWeight: 600, color: "#374151" }}>Rubix</span>{" "}
        ©2025
      </div>


    </div>
  </footer>
);

// Main Pet Profile Component
const PetProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { t, i18n } = useTranslation();
  const { getUserType, user } = useAuth();

  // Check if user came from profile page and which tab
  const fromProfile = location.state?.fromProfile || false;
  const fromProfileTab = location.state?.fromProfileTab || null;
  const profileId = location.state?.profileId || null; // For other users' profiles

  // Determine the back navigation path
  const getBackPath = () => {
    if (!fromProfile) return `/pets`;
    // If coming from pets tab (tab 2)
    if (fromProfileTab === 2) {
      // If viewing another user's profile, use their profile ID
      if (profileId) return `/social/profile/${profileId}/pets`;
      // Otherwise navigate to own profile pets tab
      return `/social/profile/pets`;
    }
    // Otherwise navigate to profile (or other user's profile)
    if (profileId) return `/social/profile/${profileId}`;
    return `/social/profile`;
  };

  // Get user type color
  const currentUserType = getUserType();
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8', // Purple
      'VETERINARIAN': '#00D4B3', // Cyan
      'SERVICE_PROVIDER': '#D65745', // Orange
      'CLINIC_MODERATOR': '#2A8AD2', // Blue
      'FIRM': '#1B3A6B', // Navy Blue
    };
    return userTypeConfig[userType] || '#8548A8'; // Default to purple
  };
  const userTypeColor = getUserTypeColor(currentUserType);

  // Extract petId from URL manually as fallback
  const getPetIdFromUrl = () => {
    const pathParts = location.pathname.split('/');
    const petIndex = pathParts.findIndex(part => part === 'pets');
    if (petIndex !== -1 && pathParts[petIndex + 1]) {
      return pathParts[petIndex + 1];
    }
    return null;
  };

  const userType = params.userType;
  const petId = params.petId && params.petId !== ':petId' ? params.petId : getPetIdFromUrl();
  const isRTL = i18n.language === "ar";
  const [activeTab, setActiveTab] = useState("petProfile");
  const validTabSlugs = useMemo(() => new Set([
    'petProfile', 'familyTree', 'medicalRecord', 'behavioralData', 'healthTrack', 'findMyPet', 'aiHealthInsights', 'guardians'
  ]), []);
  // Removed inline editing states since we're redirecting to edit page

  // Use the getPet and getCompleteMedicalHistory functions from usePets hook
  const { getPet, getCompleteMedicalHistory } = usePets();

  const [petData, setPetData] = useState(null);
  // Removed editablePetData state since we're redirecting to edit page
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [petImagePreview, setPetImagePreview] = useState(null);
  const [petImageFile, setPetImageFile] = useState(null);

  // Medical history state
  const [medicalHistory, setMedicalHistory] = useState(null);
  const [medicalHistoryLoading, setMedicalHistoryLoading] = useState(false);
  const [medicalHistoryError, setMedicalHistoryError] = useState(null);
  // Sub-tabs inside Medical Records (vaccination, medication, allergies, pastIllness, surgery, dental)
  const [medicalSubTab, setMedicalSubTab] = useState('vaccination');
  // Past visits state
  const [pastVisits, setPastVisits] = useState([]);
  const [loadingPastVisits, setLoadingPastVisits] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [attachmentsVisit, setAttachmentsVisit] = useState(null);
  const [printingVisitId, setPrintingVisitId] = useState(null);
  const [printingRxVisitId, setPrintingRxVisitId] = useState(null);

  const downloadPdfBlob = (response, defaultFileName) => {
    const disposition = response.headers?.['content-disposition'];
    let fileName = defaultFileName;
    const match = disposition?.match(/filename="?([^"]+)"?/);
    if (match) fileName = match[1];
    const blobUrl = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  const visitPdfType = (visit) => String(visit?.type || '').toLowerCase() === 'inpatient' ? 'inpatient' : 'outpatient';

  // Print the invoice for a finished visit (completed / checked out / closed)
  const handlePrintVisitInvoice = async (visit, e) => {
    e?.stopPropagation();
    if (printingVisitId) return;
    setPrintingVisitId(visit.id);
    try {
      const response = await pdfServices.generateInvoice(visit.id, visitPdfType(visit));
      downloadPdfBlob(response, `invoice-${visit.id}.pdf`);
    } catch (err) {
      console.error('Error downloading visit invoice:', err);
      showErrorToast(t('guardianDash.pastVisits.printFailed', 'Failed to download the invoice.'));
    } finally {
      setPrintingVisitId(null);
    }
  };

  // Print the prescription for a finished visit (only when one exists)
  const handlePrintVisitPrescription = async (visit, e) => {
    e?.stopPropagation();
    if (printingRxVisitId) return;
    setPrintingRxVisitId(visit.id);
    try {
      const response = await pdfServices.generatePrescriptionInvoice(visit.id, visitPdfType(visit));
      downloadPdfBlob(response, `prescription-${visit.id}.pdf`);
    } catch (err) {
      console.error('Error downloading visit prescription:', err);
      showErrorToast(t('guardianDash.pastVisits.printFailed', 'Failed to download the prescription.'));
    } finally {
      setPrintingRxVisitId(null);
    }
  };
  // keys array for keyboard navigation and consistent ordering
  const medicalTabsKeys = useMemo(() => ['vaccination', 'medication', 'allergy', 'pastIllness', 'surgery', 'dental', 'dewormingAntiFlea', 'pastVisits'], []);

  // Derived flag: is the current viewer the pet owner?
  // We compare the owner ID fields to ensure invited guardians don't get owner privileges
  const isOwner = petData && user ? 
    String(petData.owner_id || petData.ownerId || petData.user_id || petData.userId) === String(user.id) : false;

  // Check if the current user is a guardian
  const isGuardian = useMemo(() => {
    console.log('🔍 Checking if user is guardian:', { petData: petData, userId: user?.id, guardians: petData?.guardians });
    if (!petData?.guardians || !user?.id) return false;
    const result = petData.guardians.some(g => {
      const guardianId = g.user_id || g.id;
      return String(guardianId) === String(user.id);
    });
    console.log('isGuardian result:', result);
    return result;
  }, [petData?.guardians, user?.id]);

  // User can manage the pet if they are the owner OR a guardian
  const canManagePet = isOwner || isGuardian;

  // Vets and clinic moderators can view the full pet profile (but not edit)
  const isVetOrClinicMod = currentUserType === USER_TYPES.VETERINARIAN || currentUserType === USER_TYPES.CLINIC_MODERATOR;
  const canViewPetProfile = canManagePet || isVetOrClinicMod;

  console.log('🔐 Access:', { isOwner, isGuardian, canManagePet, isVetOrClinicMod, canViewPetProfile });

  // Sync activeTab with URL :tab param, but restrict non-guardians/non-owners to petProfile
  useEffect(() => {
    const tabFromUrl = params.tab;
    if (!tabFromUrl) return;

    // Only allow access to other tabs if user is owner OR guardian
    // Vets/clinic mods get full read access to all tabs
    if (!canViewPetProfile && tabFromUrl !== "petProfile") {
      setActiveTab("petProfile");
      navigate(`/pets/${petId}/view`, { replace: true });
      return;
    }

    if (validTabSlugs.has(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [params.tab, validTabSlugs, canManagePet, canViewPetProfile, petId, navigate]);

  // Sync medicalSubTab with URL ?subTab query param (e.g. from vaccination reminder notification)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const subTabFromUrl = searchParams.get('subTab');
    if (subTabFromUrl && medicalTabsKeys.includes(subTabFromUrl)) {
      setMedicalSubTab(subTabFromUrl);
    }
  }, [location.search, medicalTabsKeys]);

  useEffect(() => {
    const fetchPetData = async () => {
      if (!petId || petId === ':petId') {
        setLoading(false);
        setError('Invalid pet ID');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const result = await getPet(petId);

        if (result.success && result.data) {
          const pet = result.data;

          // Fetch guardians separately if not included in response
          if (!pet.guardians) {
            try {
              const guardiansResponse = await petGuardianshipAPI.getPetGuardians(petId);
              const guardians = guardiansResponse.data?.data || guardiansResponse.data || [];
              pet.guardians = guardians;
              console.log('✅ Fetched guardians separately:', guardians);
            } catch (guardErr) {
              console.warn('Could not fetch guardians:', guardErr);
              pet.guardians = [];
            }
          }

          setPetData(pet);
          // Prefer primaryImageUrl (cover) and fall back to transformed image field
          setPetImagePreview(pet.primaryImageUrl || pet.image || null);
        } else {
          setError(result.error || "Failed to fetch pet data");
          console.error("Failed to fetch pet:", result.error);
        }
      } catch (err) {
        console.error("Error fetching pet data:", err);
        setError("Failed to fetch pet data");
      } finally {
        setLoading(false);
      }
    };

    fetchPetData();
  }, [petId]);

  // Fetch medical history when medical record tab is accessed (owners, guardians, and vets)
  useEffect(() => {
    if (
      activeTab === "medicalRecord" &&
      petId &&
      canViewPetProfile &&
      !medicalHistory &&
      !medicalHistoryLoading
    ) {
      fetchMedicalHistory();
    }
  }, [activeTab, petId, canViewPetProfile, medicalHistory, medicalHistoryLoading]);

  // Note: medicalSubTab is now kept in component state only and not persisted to the URL

  // Keyboard navigation for sub-tabs (Left/Right arrows)
  useEffect(() => {
    if (activeTab !== 'medicalRecord') return;
    const handleKey = (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      // only act when not focusing an input or textarea
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || document.activeElement?.isContentEditable) return;
      e.preventDefault();
      const idx = medicalTabsKeys.indexOf(medicalSubTab);
      if (idx === -1) return;
      const nextIdx = e.key === 'ArrowRight' ? (idx + 1) % medicalTabsKeys.length : (idx - 1 + medicalTabsKeys.length) % medicalTabsKeys.length;
      setMedicalSubTab(medicalTabsKeys[nextIdx]);
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [activeTab, medicalSubTab, medicalTabsKeys]);

  // Removed handleImageChange and handleInputChange since we're redirecting to edit page
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
  };
  const handleEditSection = (section) => {
    // Navigate to the edit page instead of inline editing
    navigate(`/pets/${petId}/edit`, { state: { fromProfile, fromProfileTab } });
  };

  // Fetch past visits for the pet
  const fetchPastVisits = async () => {
    if (!petId) return;
    setLoadingPastVisits(true);
    try {
      const response = await petAPI.getPetVisits(petId);
      if (response.data.status === 'success') {
        const visits = response.data.data || [];
        // Only show visits that are checked out, completed, or closed
        const filtered = visits.filter(v =>
          !['active', 'admitted', 'registered', 'pending', 'in_progress', 'ongoing', 'cancelled', 'canceled'].includes(String(v.status || '').toLowerCase())
        );
        setPastVisits(filtered);
      } else if (Array.isArray(response.data)) {
        const filtered = response.data.filter(v =>
          !['active', 'admitted', 'registered', 'pending', 'in_progress', 'ongoing', 'cancelled', 'canceled'].includes(String(v.status || '').toLowerCase())
        );
        setPastVisits(filtered);
      }
    } catch (err) {
      console.error('Error fetching past visits:', err);
      setPastVisits([]);
    } finally {
      setLoadingPastVisits(false);
    }
  };

  // Fetch past visits when pastVisits tab is active
  useEffect(() => {
    if (medicalSubTab === 'pastVisits' && petId && activeTab === 'medicalRecord') {
      fetchPastVisits();
    }
  }, [medicalSubTab, petId, activeTab]);

  // Fetch complete medical history for the pet
  const fetchMedicalHistory = async () => {
    if (!petId) {
      console.error('No pet ID available');
      return;
    }

    try {
      setMedicalHistoryLoading(true);
      setMedicalHistoryError(null);

      const result = await getCompleteMedicalHistory(petId);

      if (result.success) {
        // Handle the actual API response structure
        let medicalData = result.data.data || result.data;

        // If deworming data is missing, fetch it separately and merge it
        if (!medicalData.deworming_anti_flea_history &&
          !medicalData.deworming_anti_flea &&
          !medicalData.dewormingAntiFleaHistory) {
          try {
            const dewormingRes = await api.get('/api/v1/pet-health/deworming-anti-flea-history', {
              params: { pet_id: petId }
            });
            const dewormingData = dewormingRes.data?.data || dewormingRes.data || [];
            medicalData = {
              ...medicalData,
              deworming_anti_flea_history: Array.isArray(dewormingData) ? dewormingData : []
            };
          } catch (err) {
            // Set empty array if fetch fails
            medicalData = {
              ...medicalData,
              deworming_anti_flea_history: []
            };
          }
        }

        setMedicalHistory(medicalData);
        return medicalData;
      } else {
        console.error('Failed to fetch medical history:', result.error);
        setMedicalHistoryError(result.error || 'Failed to fetch medical history');
        return null;
      }
    } catch (error) {
      console.error('Error fetching complete medical history:', error);
      setMedicalHistoryError('Failed to fetch medical history');
      return null;
    } finally {
      setMedicalHistoryLoading(false);
    }
  };

  // Refresh medical history data
  const refreshMedicalHistory = async () => {
    setMedicalHistory(null); // Clear existing data
    return await fetchMedicalHistory();
  };

  // Removed inline editing functions since we're redirecting to edit page

  const baseTabs = [
    {
      key: "petProfile",
      name: t("petProfile.tabs.petProfile"),
    },
    {
      key: "medicalRecord",
      name: t("petProfile.tabs.medicalRecord"),
    },
    {
      key: "guardians",
      name: t("petProfile.tabs.guardians"),
    },
    {
      key: "behavioralData",
      name: t("petProfile.tabs.behavioralData"),
    },
    {
      key: "familyTree",
      name: t("petProfile.tabs.familyTree"),
    },
    {
      key: "healthTrack",
      name: t("petProfile.tabs.healthTrack"),
    },
    { key: "findMyPet", name: t("petProfile.tabs.findMyPet") },
    {
      key: "aiHealthInsights",
      name: t("petProfile.tabs.aiHealthInsights"),
    },
  ];

  const tabs = canViewPetProfile
    ? baseTabs
    : baseTabs.filter((tab) => tab.key === "petProfile");

  const handleTabClick = (tabKey) => {
    setActiveTab(tabKey);
    if (petId) {
      navigate(`/pets/${petId}/view/${tabKey}`);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "petProfile":
        return (
          <PetProfileView
            petData={petData}
            handleEditSection={handleEditSection}
            isOwner={isOwner}
          />
        );
      case "guardians":
        return (
          <GuardianshipList
            petId={petId}
            isOwner={isOwner}
          />
        );
      case "medicalRecord":
        // Show loading state for medical history
        if (medicalHistoryLoading) {
          return (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  border: "4px solid #E5E7EB",
                  borderTop: `4px solid ${userTypeColor}`,
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto 16px auto",
                }}
              ></div>
              <h3 style={{ color: "#6B7280" }}>Loading medical records...</h3>
            </div>
          );
        }

        // Show error state for medical history
        if (medicalHistoryError) {
          return (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <h3 style={{ color: "#EF4444", marginBottom: "16px" }}>
                Error loading medical records
              </h3>
              <p style={{ color: "#6B7280", marginBottom: "24px" }}>{medicalHistoryError}</p>
              <button
                onClick={fetchMedicalHistory}
                style={{
                  padding: "12px 24px",
                  background: userTypeColor,
                  color: "#FFFFFF",
                  border: "none",
                  borderRadius: "12px",
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Try Again
              </button>
            </div>
          );
        }

        return (
          <>
            {/* Sub-tabs for medical records: buttons for desktop, select for small screens */}
            <div className="petprofile-medical-subtabs" style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div className="petprofile-medical-subtabs-wrapper" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { key: 'vaccination', label: t('petProfile.medicalRecords.vaccinationHistory') },
                  { key: 'medication', label: t('petProfile.medicalRecords.medicationHistory') },
                  { key: 'allergy', label: t('petProfile.medicalRecords.currentAllergies') },
                  { key: 'pastIllness', label: t('petProfile.medicalRecords.pastIllness') },
                  { key: 'surgery', label: t('petProfile.medicalRecords.surgicalHistory') },
                  { key: 'dental', label: t('petProfile.medicalRecords.dentalRecords') },
                  { key: 'dewormingAntiFlea', label: t('petProfile.medicalRecords.dewormingAntiFleaHistory') },
                  { key: 'pastVisits', label: t('receptionInpatients.details.tabs.pastVisits', { defaultValue: 'Past Visits' }) }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setMedicalSubTab(tab.key)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 10,
                      border: medicalSubTab === tab.key ? `1px solid ${userTypeColor}` : '1px solid transparent',
                      background: medicalSubTab === tab.key ? '#fff' : '#F8F9FA',
                      color: medicalSubTab === tab.key ? userTypeColor : '#374151',
                      cursor: 'pointer',
                      fontWeight: medicalSubTab === tab.key ? 600 : 500
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* removed small-screen select per request */}
            </div>

            {/* Render only the active medical record view */}
            <style>{`@keyframes fadeInUp { from { opacity: 0; transform: translateY(6px);} to {opacity:1; transform: translateY(0);} }`}</style>

            <Suspense fallback={<div style={{ padding: 20 }}>Loading table...</div>}>
              {medicalSubTab === 'vaccination' && (
                <div style={{ animation: 'fadeInUp 180ms ease-out' }}>
                  <MedicalRecordView
                    title={t('petProfile.medicalRecords.vaccinationHistory')}
                    icon="/icons/vacc.png"
                    columns={[
                      { field: 'Veterinarian', label: t('petProfile.medicalRecords.columns.veterinarian') },
                      { field: 'Vaccine Name', label: t('petProfile.medicalRecords.columns.vaccineName') },
                      { field: 'Date Administered', label: t('petProfile.medicalRecords.columns.dateAdministered') },
                      { field: 'Next Due Date', label: t('petProfile.medicalRecords.columns.nextDueDate') },
                      { field: 'Note', label: t('petProfile.medicalRecords.columns.note') },
                    ]}
                    fetchUrl="/api/v1/pet-health/vaccinations"
                    formType="vaccination"
                    petData={petData}
                    showActions={canManagePet}
                    medicalHistoryData={medicalHistory?.vaccination_history || []}
                    useSharedData={true}
                    onMedicalHistoryChange={refreshMedicalHistory}
                  />
                </div>
              )}

              {medicalSubTab === 'medication' && (
                <div style={{ animation: 'fadeInUp 180ms ease-out' }}>
                  <MedicalRecordView
                    title={t('petProfile.medicalRecords.medicationHistory')}
                    icon="/icons/med.png"
                    columns={[
                      { field: 'Veterinarian', label: t('petProfile.medicalRecords.columns.veterinarian') },
                      { field: 'Medication Name', label: t('petProfile.medicalRecords.columns.medicationName') },
                      { field: 'Dosage', label: t('petProfile.medicalRecords.columns.dosage') },
                      { field: 'Start Date', label: t('petProfile.medicalRecords.columns.startDate') },
                      { field: 'Currently Taking', label: t('petProfile.medicalRecords.columns.currentlyTaking') },
                    ]}
                    fetchUrl="/api/v1/pet-health/medications"
                    formType="medication"
                    petData={petData}
                    showActions={canManagePet}
                    medicalHistoryData={medicalHistory?.medication_history || []}
                    useSharedData={true}
                    onMedicalHistoryChange={refreshMedicalHistory}
                  />
                </div>
              )}

              {medicalSubTab === 'allergy' && (
                <div style={{ animation: 'fadeInUp 180ms ease-out' }}>
                  <MedicalRecordView
                    title={t('petProfile.medicalRecords.currentAllergies')}
                    icon="/icons/aller.png"
                    columns={[
                      { field: 'Allergen', label: t('petProfile.medicalRecords.columns.allergen') },
                      { field: 'Reaction', label: t('petProfile.medicalRecords.columns.reaction') },
                      { field: 'Severity', label: t('petProfile.medicalRecords.columns.severity') },
                      { field: 'Note', label: t('petProfile.medicalRecords.columns.note') },
                    ]}
                    fetchUrl="/api/v1/pet-health/allergies"
                    formType="allergies"
                    petData={petData}
                    showActions={canManagePet}
                    medicalHistoryData={medicalHistory?.current_allergies || []}
                    useSharedData={true}
                    onMedicalHistoryChange={refreshMedicalHistory}
                  />
                </div>
              )}

              {medicalSubTab === 'pastIllness' && (
                <div style={{ animation: 'fadeInUp 180ms ease-out' }}>
                  <MedicalRecordView
                    title={t('petProfile.medicalRecords.pastIllness')}
                    icon="/icons/chron.png"
                    columns={[
                      { field: 'Illness Name', label: t('petProfile.medicalRecords.columns.illnessName') },
                      { field: 'Veterinarian', label: t('petProfile.medicalRecords.columns.veterinarian') },
                      { field: 'Final Diagnosis', label: t('petProfile.medicalRecords.columns.finalDiagnosis') },
                      { field: 'Diagnosis Date', label: t('petProfile.medicalRecords.columns.diagnosisDate') },
                      { field: 'Actions', label: t('petProfile.medicalRecords.columns.actions') },
                    ]}
                    fetchUrl="/api/v1/pet-health/past-illness-history"
                    formType="pastIllness"
                    petData={petData}
                    showActions={canManagePet}
                    medicalHistoryData={medicalHistory?.past_illness_history || []}
                    useSharedData={true}
                    onMedicalHistoryChange={refreshMedicalHistory}
                  />
                </div>
              )}

              {medicalSubTab === 'surgery' && (
                <div style={{ animation: 'fadeInUp 180ms ease-out' }}>
                  <MedicalRecordView
                    title={t('petProfile.medicalRecords.surgicalHistory')}
                    icon="/icons/surg.png"
                    columns={[
                      { field: 'Veterinarian', label: t('petProfile.medicalRecords.columns.veterinarian') },
                      { field: 'Surgery Name', label: t('petProfile.medicalRecords.columns.surgeryName') },
                      { field: 'Note', label: t('petProfile.medicalRecords.columns.note') },
                      { field: 'Date', label: t('petProfile.medicalRecords.columns.date') },
                    ]}
                    fetchUrl="/api/v1/pet-health/surgeries"
                    formType="surgery"
                    petData={petData}
                    showActions={canManagePet}
                    medicalHistoryData={medicalHistory?.surgical_history || []}
                    useSharedData={true}
                    onMedicalHistoryChange={refreshMedicalHistory}
                  />
                </div>
              )}

              {medicalSubTab === 'dental' && (
                <div style={{ animation: 'fadeInUp 180ms ease-out' }}>
                  <MedicalRecordView
                    title={t('petProfile.medicalRecords.dentalRecords')}
                    icon="/icons/dent.png"
                    columns={[
                      { field: 'Veterinarian', label: t('petProfile.medicalRecords.columns.veterinarian') },
                      { field: 'Dental Procedure', label: t('petProfile.medicalRecords.columns.dentalProcedure') },
                      { field: 'Note', label: t('petProfile.medicalRecords.columns.note') },
                      { field: 'Date', label: t('petProfile.medicalRecords.columns.date') },
                    ]}
                    fetchUrl="/api/v1/pet-health/dental-records"
                    formType="dental"
                    petData={petData}
                    showActions={canManagePet}
                    medicalHistoryData={medicalHistory?.dental_records || []}
                    useSharedData={true}
                    onMedicalHistoryChange={refreshMedicalHistory}
                  />
                </div>
              )}

              {medicalSubTab === 'dewormingAntiFlea' && (
                <div style={{ animation: 'fadeInUp 180ms ease-out' }}>
                  <MedicalRecordView
                    title={t('petProfile.medicalRecords.dewormingAntiFleaHistory')}
                    icon="/icons/vacc.png"
                    columns={[
                      { field: 'Veterinarian', label: t('petProfile.medicalRecords.columns.veterinarian') },
                      { field: 'Type', label: t('petProfile.medicalRecords.columns.type') },
                      { field: 'Date', label: t('petProfile.medicalRecords.columns.date') },
                      { field: 'Next Due Date', label: t('petProfile.medicalRecords.columns.nextDueDate') },
                      { field: 'Note', label: t('petProfile.medicalRecords.columns.note') },
                    ]}
                    fetchUrl="/api/v1/pet-health/deworming-anti-flea-history"
                    formType="dewormingAntiFlea"
                    petData={petData}
                    showActions={canManagePet}
                    medicalHistoryData={
                      medicalHistory?.deworming_anti_flea_history ||
                      medicalHistory?.deworming_anti_flea ||
                      medicalHistory?.dewormingAntiFleaHistory ||
                      []
                    }
                    useSharedData={true}
                    onMedicalHistoryChange={refreshMedicalHistory}
                  />
                </div>
              )}

              {medicalSubTab === 'pastVisits' && (
                <div style={{ animation: 'fadeInUp 180ms ease-out' }}>
                  <div style={{
                    background: '#fff',
                    borderRadius: '12px',
                    padding: '24px',
                    border: `1px solid ${userTypeColor}20`
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '20px'
                    }}>
                      <h3 style={{
                        fontSize: '20px',
                        fontWeight: 600,
                        color: '#111827',
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        <i className="fas fa-history" style={{ color: userTypeColor }}></i>
                        {t('receptionInpatients.details.tabs.pastVisits', { defaultValue: 'Past Visits' })}
                      </h3>
                    </div>

                    {loadingPastVisits ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          border: `4px solid #E5E7EB`,
                          borderTop: `4px solid ${userTypeColor}`,
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          margin: '0 auto 16px'
                        }}></div>
                        <p style={{ color: '#6B7280' }}>{t('common.loading')}</p>
                      </div>
                    ) : pastVisits.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {pastVisits.map((visit, index) => {
                          const formatDate = (dateString) => {
                            if (!dateString) return '---';
                            try {
                              const date = new Date(dateString);
                              if (isNaN(date.getTime())) return dateString;
                              return date.toLocaleDateString(i18n.language, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              });
                            } catch {
                              return dateString;
                            }
                          };

                          // The backend sometimes returns an unresolved
                          // translation key (e.g. "api_responses.status_checkout")
                          // as the status — normalize to a known value.
                          const normalizeStatus = (raw) => {
                            const s = String(raw || '').toLowerCase();
                            if (s.includes('api_responses')) {
                              if (s.includes('checkout')) return 'checkout';
                              if (s.includes('active')) return 'active';
                              return 'completed';
                            }
                            if (s.includes('checkout') || s.includes('checked')) return 'checkout';
                            if (s.includes('complete')) return 'completed';
                            if (s.includes('closed')) return 'closed';
                            if (s.includes('active') || s.includes('admitted')) return 'active';
                            return s || 'completed';
                          };

                          const statusKey = normalizeStatus(visit.status);
                          const statusConfig = {
                            completed: { label: t('visitReports.status.completed', { defaultValue: 'Completed' }), bg: '#d1fae5', color: '#065f46' },
                            checkout: { label: t('visitReports.status.checkout', { defaultValue: 'Checked Out' }), bg: '#fef3c7', color: '#92400e' },
                            closed: { label: t('visitReports.status.closed', { defaultValue: 'Closed' }), bg: '#e5e7eb', color: '#374151' },
                            active: { label: t('visitReports.status.active', { defaultValue: 'Active' }), bg: '#ccfbf1', color: '#0f766e' },
                          };
                          const statusBadge = statusConfig[statusKey] || statusConfig.completed;

                          return (
                            <div
                              key={visit.id || index}
                              onClick={() => setSelectedVisit(visit)}
                              style={{
                                background: '#F9FAFB',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                padding: '16px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                ':hover': {
                                  borderColor: userTypeColor,
                                  boxShadow: `0 4px 6px -1px ${userTypeColor}20`
                                }
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = userTypeColor;
                                e.currentTarget.style.boxShadow = `0 4px 6px -1px ${userTypeColor}20`;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#E5E7EB';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                marginBottom: '12px'
                              }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    color: '#111827',
                                    marginBottom: '4px',
                                    textTransform: 'capitalize'
                                  }}>
                                    {visit.type === 'inpatient'
                                      ? t('receptionInpatients.title', { defaultValue: 'Inpatient Visit' })
                                      : t('receptionOutpatients.title', { defaultValue: 'Outpatient Visit' })}
                                    {visit.serial_num && (
                                      <span style={{
                                        fontSize: '0.85em',
                                        color: '#6B7280',
                                        marginLeft: '8px',
                                        fontWeight: 400
                                      }}>
                                        #{visit.serial_num}
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#6B7280' }}>
                                    {formatDate(visit.visit_date || visit.created_at)}
                                  </div>
                                </div>
                                {visit.doctor_name && (
                                  <div style={{
                                    fontSize: '14px',
                                    color: '#6B7280',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}>
                                    <i className="fas fa-user-md" style={{ fontSize: '12px' }}></i>
                                    {visit.doctor_name}
                                  </div>
                                )}
                              </div>
                              <div style={{
                                display: 'flex',
                                gap: '16px',
                                flexWrap: 'wrap',
                                marginBottom: '8px'
                              }}>
                                <span style={{ fontSize: '13px', color: '#6B7280', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                  <strong>{t('common.status', { defaultValue: 'Status' })}:</strong>
                                  <span style={{
                                    padding: '2px 8px',
                                    backgroundColor: statusBadge.bg,
                                    color: statusBadge.color,
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {statusBadge.label}
                                  </span>
                                </span>
                                {visit.reason && (
                                  <span style={{ fontSize: '13px', color: '#6B7280' }}>
                                    <strong>{t('receptionOutpatients.details.reason', { defaultValue: 'Reason' })}:</strong> {visit.reason}
                                  </span>
                                )}
                              </div>
                              <div
                                style={{ display: 'flex', gap: '8px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #E5E7EB' }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  onClick={(e) => handlePrintVisitInvoice(visit, e)}
                                  disabled={printingVisitId === visit.id}
                                  style={{
                                    background: '#ffffff',
                                    border: `1.5px solid #E5E7EB`,
                                    borderRadius: '8px',
                                    padding: '6px 14px',
                                    fontSize: '12.5px',
                                    fontWeight: 600,
                                    color: userTypeColor,
                                    cursor: printingVisitId === visit.id ? 'not-allowed' : 'pointer',
                                    opacity: printingVisitId === visit.id ? 0.6 : 1,
                                    fontFamily: 'inherit',
                                    transition: 'all 0.15s ease'
                                  }}
                                >
                                  {printingVisitId === visit.id
                                    ? t('common.loading', { defaultValue: 'Loading...' })
                                    : t('guardianDash.pastVisits.invoice', { defaultValue: 'Invoice' })}
                                </button>
                                {typeof visit.prescription === 'string' && visit.prescription.trim().length > 0 && (
                                  <button
                                    type="button"
                                    onClick={(e) => handlePrintVisitPrescription(visit, e)}
                                    disabled={printingRxVisitId === visit.id}
                                    style={{
                                      background: '#ffffff',
                                      border: `1.5px solid #E5E7EB`,
                                      borderRadius: '8px',
                                      padding: '6px 14px',
                                      fontSize: '12.5px',
                                      fontWeight: 600,
                                      color: userTypeColor,
                                      cursor: printingRxVisitId === visit.id ? 'not-allowed' : 'pointer',
                                      opacity: printingRxVisitId === visit.id ? 0.6 : 1,
                                      fontFamily: 'inherit',
                                      transition: 'all 0.15s ease'
                                    }}
                                  >
                                    {printingRxVisitId === visit.id
                                      ? t('common.loading', { defaultValue: 'Loading...' })
                                      : t('guardianDash.pastVisits.prescription', { defaultValue: 'Prescription' })}
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setAttachmentsVisit(visit); }}
                                  style={{
                                    background: '#ffffff',
                                    border: `1.5px solid #E5E7EB`,
                                    borderRadius: '8px',
                                    padding: '6px 14px',
                                    fontSize: '12.5px',
                                    fontWeight: 600,
                                    color: userTypeColor,
                                    cursor: 'pointer',
                                    fontFamily: 'inherit',
                                    transition: 'all 0.15s ease',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}
                                >
                                  <i className="fas fa-paperclip"></i>
                                  {t('receptionOutpatients.attachments.header.title', { defaultValue: 'Attachments' })}
                                </button>
                              </div>
                              <div style={{
                                marginTop: '8px',
                                fontSize: '13px',
                                color: userTypeColor,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                <i className="fas fa-eye"></i>
                                {visit.can_view_full_soap === false
                                  ? t('common.viewFinalDiagnosis', { defaultValue: 'View Final Diagnosis' })
                                  : t('common.viewDetails', { defaultValue: 'View SOAP' })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{
                        textAlign: 'center',
                        padding: '40px',
                        color: '#6B7280'
                      }}>
                        {t('common.noData', { defaultValue: 'No past visits found' })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Suspense>

            {/* SOAP Details Modal for Past Visits */}
            {selectedVisit && selectedVisit.can_view_full_soap !== false && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.6)',
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
              }} onClick={() => setSelectedVisit(null)}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  width: '90%',
                  maxWidth: '900px',
                  maxHeight: '90vh',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }} onClick={e => e.stopPropagation()}>
                  <div style={{
                    padding: '16px 24px',
                    borderBottom: '1px solid #E5E7EB',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: '#F9FAFB'
                  }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                      {t('receptionInpatients.details.tabs.soapNotes', { defaultValue: 'SOAP Notes' })} - {(() => {
                        const formatDate = (dateString) => {
                          if (!dateString) return '---';
                          try {
                            const date = new Date(dateString);
                            if (isNaN(date.getTime())) return dateString;
                            return date.toLocaleDateString(i18n.language, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            });
                          } catch {
                            return dateString;
                          }
                        };
                        return formatDate(selectedVisit.visit_date || selectedVisit.created_at);
                      })()}
                    </h3>
                    <button
                      onClick={() => setSelectedVisit(null)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '24px',
                        cursor: 'pointer',
                        color: '#6B7280',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#FEE2E2';
                        e.target.style.color = '#DC2626';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#6B7280';
                      }}
                    >
                      &times;
                    </button>
                  </div>
                  <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                    <SOAPDetailsContent
                      isVisible={true}
                      inpatientId={String(selectedVisit.type || '').toLowerCase() === 'inpatient' ? (selectedVisit.id || selectedVisit.visit_id || selectedVisit.inpatient_visit_id || selectedVisit.inpatient_id || selectedVisit.pet_visit_id) : null}
                      outpatientId={(String(selectedVisit.type || '').toLowerCase() === 'outpatient' || !selectedVisit.type) ? (selectedVisit.id || selectedVisit.visit_id || selectedVisit.outpatient_visit_id || selectedVisit.outpatient_id || selectedVisit.pet_visit_id) : null}
                      readOnly={true}
                      hideHeader={true}
                      petId={petId}
                      services={selectedVisit.services || selectedVisit.visit_details || selectedVisit.visitDetails || []}
                      planServicesMode="fulfilledOnly"
                      visitInfo={{
                        visitDate: selectedVisit.visit_date || selectedVisit.created_at,
                        initialDiagnosis: selectedVisit.reason || '',
                        finalDiagnosis: selectedVisit.final_diag || selectedVisit.final_diagnosis || '',
                        doctorNotes: selectedVisit.doctor_notes || '',
                        prescription: selectedVisit.prescription || ''
                      }}
                      onClose={() => setSelectedVisit(null)}
                    />
                  </div>
                </div>
              </div>
            )}

            {selectedVisit && selectedVisit.can_view_full_soap === false && (
              <RestrictedVisitModal visit={selectedVisit} onClose={() => setSelectedVisit(null)} />
            )}

            {/* Attachments viewer for Past Visits (read-only) */}
            {attachmentsVisit && (
              <AttachmentsModal
                isOpen={true}
                onClose={() => setAttachmentsVisit(null)}
                readOnly={true}
                inpatientId={String(attachmentsVisit.type || '').toLowerCase() === 'inpatient'
                  ? (attachmentsVisit.id || attachmentsVisit.visit_id || attachmentsVisit.inpatient_visit_id || attachmentsVisit.inpatient_id || attachmentsVisit.pet_visit_id)
                  : null}
                outpatientId={(String(attachmentsVisit.type || '').toLowerCase() === 'outpatient' || !attachmentsVisit.type)
                  ? (attachmentsVisit.id || attachmentsVisit.visit_id || attachmentsVisit.outpatient_visit_id || attachmentsVisit.outpatient_id || attachmentsVisit.pet_visit_id)
                  : null}
              />
            )}
          </>
        );
      case "behavioralData":
        return <BehavioralDataView petData={petData} />;
      case "healthTrack":
        return (
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 20px",
            textAlign: "center",
            backgroundColor: "#FAFAFA",
            borderRadius: "12px",
            border: `2px solid ${userTypeColor}`,
            margin: "20px 0"
          }}>
            <div style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              backgroundColor: userTypeColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "20px"
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M9 12l2 2 4-4" />
                <circle cx="12" cy="12" r="10" />
              </svg>
            </div>
            <h2 style={{
              color: userTypeColor,
              marginBottom: "12px",
              fontSize: "24px",
              fontWeight: "600"
            }}>
              Health Track
            </h2>
            <p style={{
              color: "#6B7280",
              fontSize: "16px",
              marginBottom: "20px",
              maxWidth: "400px"
            }}>
              Track your pet's health metrics, weight, and vital signs over time. This feature is coming soon!
            </p>
            <div style={{
              padding: "12px 24px",
              backgroundColor: userTypeColor,
              color: "white",
              borderRadius: "8px",
              fontWeight: "600",
              fontSize: "14px"
            }}>
              {t('comingSoon')}
            </div>
          </div>
        );
      case "findMyPet":
        return (
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 20px",
            textAlign: "center",
            backgroundColor: "#FAFAFA",
            borderRadius: "12px",
            border: `2px solid ${userTypeColor}`,
            margin: "20px 0"
          }}>
            <div style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              backgroundColor: userTypeColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "20px"
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                <path d="M12 6v6l4 2" />
              </svg>
            </div>
            <h2 style={{
              color: userTypeColor,
              marginBottom: "12px",
              fontSize: "24px",
              fontWeight: "600"
            }}>
              Find My Pet
            </h2>
            <p style={{
              color: "#6B7280",
              fontSize: "16px",
              marginBottom: "20px",
              maxWidth: "400px"
            }}>
              GPS tracking and location services to help you locate your pet if they go missing. This feature is coming soon!
            </p>
            <div style={{
              padding: "12px 24px",
              backgroundColor: userTypeColor,
              color: "white",
              borderRadius: "8px",
              fontWeight: "600",
              fontSize: "14px"
            }}>
              {t('comingSoon')}
            </div>
          </div>
        );
      case "aiHealthInsights":
        return (
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 20px",
            textAlign: "center",
            backgroundColor: "#FAFAFA",
            borderRadius: "12px",
            border: `2px solid ${userTypeColor}`,
            margin: "20px 0"
          }}>
            <div style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              backgroundColor: userTypeColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "20px"
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <h2 style={{
              color: userTypeColor,
              marginBottom: "12px",
              fontSize: "24px",
              fontWeight: "600"
            }}>
              AI Health Insights
            </h2>
            <p style={{
              color: "#6B7280",
              fontSize: "16px",
              marginBottom: "20px",
              maxWidth: "400px"
            }}>
              Get AI-powered health recommendations and insights based on your pet's medical history and behavior patterns.
            </p>
            <div style={{
              padding: "12px 24px",
              backgroundColor: userTypeColor,
              color: "white",
              borderRadius: "8px",
              fontWeight: "600",
              fontSize: "14px"
            }}>
              {t('comingSoon')}
            </div>
          </div>
        );
      case "familyTree":
        return (
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 20px",
            textAlign: "center",
            backgroundColor: "#FAFAFA",
            borderRadius: "12px",
            border: `2px solid ${userTypeColor}`,
            margin: "20px 0"
          }}>
            <div style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              backgroundColor: userTypeColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "20px"
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                <path d="M12 11h4" />
                <path d="M12 16h4" />
                <path d="M8 11h.01" />
                <path d="M8 16h.01" />
              </svg>
            </div>
            <h2 style={{
              color: userTypeColor,
              marginBottom: "12px",
              fontSize: "24px",
              fontWeight: "600"
            }}>
              Family Tree
            </h2>
            <p style={{
              color: "#6B7280",
              fontSize: "16px",
              marginBottom: "20px",
              maxWidth: "400px"
            }}>
              View and manage your pet's family lineage, siblings, parents, and offspring. Track genetic information and family health history.
            </p>
            <div style={{
              padding: "12px 24px",
              backgroundColor: userTypeColor,
              color: "white",
              borderRadius: "8px",
              fontWeight: "600",
              fontSize: "14px"
            }}>
              {t('comingSoon')}
            </div>
          </div>
        );
      default:
        return <div>{t("petProfile.comingSoon")}</div>;
    }
  };

  if (loading) {
    return (
      <div className="petprofile-root petprofile-center-content" style={{ '--user-type-color': userTypeColor }}>
        <div className="petprofile-loading-container">
          <div className="petprofile-spinner"></div>
          <h2 className="petprofile-loading-title">
            {t("petProfile.loadingProfile")}
          </h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="petprofile-root petprofile-center-content" style={{ '--user-type-color': userTypeColor }}>
        <div className="petprofile-error-container">
          <h2 className="petprofile-error-title">
            {t("petProfile.errorLoadingPet")}
          </h2>
          <p className="petprofile-error-message">{error}</p>
          <div className="petprofile-error-buttons">
            <button
              onClick={() => window.location.reload()}
              className="petprofile-btn-primary"
            >
              {t("petProfile.tryAgain")}
            </button>
            <button
              onClick={() => navigate(getBackPath())}
              className="petprofile-btn-secondary"
            >
              {fromProfile ? t("petProfile.backToProfile") : t("petProfile.backToPets")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!petData) {
    return (
      <div className="petprofile-root petprofile-center-content" style={{ '--user-type-color': userTypeColor }}>
        <div className="petprofile-error-container">
          <h2 className="petprofile-error-title">
            {t("petProfile.petNotFound")}
          </h2>
          <p className="petprofile-error-message">
            {t("petProfile.petNotFoundDescription")}
          </p>
          <button
            onClick={() => navigate(getBackPath())}
            className="petprofile-btn-primary"
          >
            {fromProfile ? t("petProfile.backToProfile") : t("petProfile.backToPets")}
          </button>
        </div>
      </div>
    );
  }

  // Removed renderField function since we're redirecting to edit page

  return (
    <div className="petprofile-root" style={{ '--user-type-color': userTypeColor }}>
      <Navbar userType={userType} />

      <main className={`petprofile-main ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="addpet-container">
          {/* Header Section with Back Button */}
          <div className="addpet-header">
            <button
              onClick={() => navigate(getBackPath())}
              className="addpet-back-btn"
              style={{
                '--user-type-color': userTypeColor,
                flexDirection: isRTL ? 'row-reverse' : 'row'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              {fromProfile ? t("petProfile.backToProfile") : t("petProfile.backToPets")}
            </button>
          </div>

          {/* Pet Image */}
          <div className="petprofile-image-container">
            <div className="petprofile-image-wrapper">
              {petImagePreview && !petImagePreview.includes("no-image-pet") ? (
                <img
                  src={petImagePreview}
                  alt="Pet Preview"
                  className="petprofile-image"
                  onError={() => { setPetImagePreview(null); }}
                />
              ) : (
                <div className="petprofile-image-paw" aria-hidden="true" />
              )}
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="petprofile-tabs-container" style={{ scrollBehavior: 'smooth' }}>
            <div className="petprofile-tabs-wrapper">
              {tabs.map((tab, index) => (
                <button
                  key={index}
                  onClick={() => handleTabClick(tab.key)}
                  className={`petprofile-tab ${activeTab === tab.key ? 'active' : ''}`}
                >
                  <span>{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {renderTabContent()}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PetProfile;
